import { useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { addPositiveToast } from 'modules/toastContainer';
import { Controller, useForm, UseFormSetValue } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs';
import {
  EditLocalePayload,
  ProjectSettingsFormSchemaType,
  useProjectSettingsModalsContext,
} from '../../contexts';
import { Modal, MODAL_SIZES, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { z } from 'zod';
import { FIELD_NAMES, NEW_TAB_PROPS } from '../../constants';
import { getUniqueLocales } from '../../utils';
import { Link } from 'ui/links';
import { Select } from 'ui/selectv2/Select';
import { useLocaleOptions } from './hooks/useLocaleOptions';

const { LOCALES, MAPPED_LOCALES } = FIELD_NAMES;
const DEFAULT_UPDATE_LOCALE_FORM_VALUES = { newLocale: '', newLanguage: '' };

const UPDATE_LOCALE_MODAL_TEXT = {
  localeLabel: 'Name',
  localeLabelDescription: (
    <>
      Enter a name for your locale. We recommend using a two-letter ISO code for clarity and
      consistency.{' '}
      <Link
        {...NEW_TAB_PROPS}
        hasOpenNewTabIcon
        size="caption"
        href="https://support.iterable.com/hc/en-us/articles/213279923"
      >
        Learn more
      </Link>
    </>
  ),
  languageLabel: 'Language',
  languageLabelDescription: (
    <>
      Select a{' '}
      <Link
        {...NEW_TAB_PROPS}
        hasOpenNewTabIcon
        size="caption"
        href="https://developers.facebook.com/docs/whatsapp/business-management-api/message-templates/supported-languages"
      >
        supported language
      </Link>{' '}
      to associate with this locale, which is used by Iterable to communicate with other platforms
      such as WhatsApp.
    </>
  ),
  languagePlaceholder: 'Select a language',
};

/** Locale name is not editable, ony language */
const getAddLocaleFormSchema = (locales: string[], skipNameCheck: boolean = false) =>
  z.object({
    newLocale: z
      .string()
      .min(1, 'Name is required to create a new locale')
      .refine(
        (val: string) => skipNameCheck || !locales.some((locale) => locale === val.trim()),
        'One or more locales with the same name already exists in your project.',
      ),
    newLanguage: z.string().min(1, 'Language is required to create a new locale'),
  });

type WALocaleModalProps = {
  /** Current locales provided by project.extendedProjectSettings */
  locales: string[];
  mappedLocales: {
    tag: string;
    whatsAppInfobipLocale: string;
  }[];
  /** Utility function provided by Project Settings page form context */
  setValue: UseFormSetValue<ProjectSettingsFormSchemaType>;
};

export const WALocaleModal = ({ locales, mappedLocales, setValue }: WALocaleModalProps) => {
  const dispatch = useDispatch();
  const { modalState, hideModal } = useProjectSettingsModalsContext<EditLocalePayload>();
  const { locale, language } = modalState?.payload || {};
  const isEdit = !!locale;
  const schema = useMemo(() => getAddLocaleFormSchema(locales, isEdit), [locales, isEdit]);

  /** Local form used for adding `locales` to existing values */
  const {
    control,
    formState: { isSubmitted, isValid } = {},
    handleSubmit,
    watch,
  } = useForm({
    defaultValues: isEdit
      ? { newLocale: locale, newLanguage: language ?? '' }
      : DEFAULT_UPDATE_LOCALE_FORM_VALUES,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: zodResolver(schema),
  });

  const { options, categoryLabels } = useLocaleOptions(watch('newLocale'));

  const onSubmit = ({ newLocale, newLanguage }: { newLocale: string; newLanguage: string }) => {
    const uniqueLocales = getUniqueLocales(locales, newLocale);
    const mappedLocaleIndex = mappedLocales.findIndex((o) => o.tag === newLocale);

    //Add new language to locale/language mapping
    setValue(LOCALES, uniqueLocales, { shouldDirty: true });
    if (mappedLocaleIndex !== -1) {
      const newMappedLocales = [...mappedLocales];
      newMappedLocales[mappedLocaleIndex] = { tag: newLocale, whatsAppInfobipLocale: newLanguage };
      setValue(MAPPED_LOCALES, newMappedLocales, { shouldDirty: true });
    } else {
      setValue(
        MAPPED_LOCALES,
        [...mappedLocales, { tag: newLocale, whatsAppInfobipLocale: newLanguage }],
        { shouldDirty: true },
      );
    }

    dispatch(addPositiveToast('Locale was added successfully. Remember to save your changes.'));
    hideModal();
  };

  return (
    <Modal id="add-locale-modal" onClose={hideModal} isModalOpen size={MODAL_SIZES.SMALL}>
      <ModalHeader>{isEdit ? 'Edit' : 'Add new'} locale</ModalHeader>
      <ModalBody>
        <form id="add-new-locale-form" onSubmit={handleSubmit(onSubmit)}>
          <Controller
            control={control}
            name="newLocale"
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              const errorMessage = error?.message;
              return (
                <FormField
                  inputId="name"
                  labelText={UPDATE_LOCALE_MODAL_TEXT.localeLabel}
                  hasError={!!errorMessage}
                  errorText={errorMessage}
                  descriptionText={UPDATE_LOCALE_MODAL_TEXT.localeLabelDescription}
                >
                  <InputText
                    name="name"
                    id="name"
                    value={value}
                    onChange={onChange}
                    autoFocus
                    isDisabled={isEdit}
                  />
                </FormField>
              );
            }}
          />
          <Controller
            control={control}
            name="newLanguage"
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              const errorMessage = error?.message;
              return (
                <FormField
                  inputId="language"
                  labelText={UPDATE_LOCALE_MODAL_TEXT.languageLabel}
                  descriptionText={UPDATE_LOCALE_MODAL_TEXT.languageLabelDescription}
                >
                  <Select
                    usePortal
                    id="language"
                    options={options}
                    showCategoryDivider
                    placeholder={UPDATE_LOCALE_MODAL_TEXT.languagePlaceholder}
                    onSelect={([{ value }]) => onChange(value)}
                    value={options.filter((option) => option.value === value)}
                    categoryLabels={categoryLabels}
                    errorMsg={errorMessage}
                  />
                </FormField>
              );
            }}
          />
        </form>
      </ModalBody>
      <ModalFooter onClose={hideModal}>
        <PrimaryButton
          form="add-new-locale-form"
          isDisabled={!!isSubmitted && !isValid}
          size={BUTTON_SIZES.LARGE}
          type="submit"
        >
          {isEdit ? 'Save changes' : 'Add locale'}
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
